<script setup>
// core
import {ref, reactive, computed, getCurrentInstance, onMounted, watchEffect} from 'vue'
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";

// components
import CallNumberView from "@/components/Telephony/CallNumberView.vue";
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue";

// validate
import {validateWebhookUrl} from "@/helpers/dialogValidation";

const isLoading = ref(false)
const {proxy} = getCurrentInstance()
const store = useStore()
const {t: $t} = useI18n()
const form = reactive({
    incoming: "",
})

const initialIncoming = ref("")

const dialogParams = computed(() => store.state.dialogParams);

const error = computed(() => validateWebhookUrl(form.incoming, $t))

const isSubmittable = computed(
    () => !!form.incoming?.trim() && !error.value && form.incoming !== initialIncoming.value
)

const addWebhook = async () => {
    isLoading.value = true
    try {
        await store.dispatch('telephony/addWebhook', {
            number: dialogParams.value?.number,
            webhooks: [{
                type: 'inbox',
                url: form.incoming,
            }],
            moshaToast: proxy.$moshaToast,
        })
    } finally {
        isLoading.value = false
    }
}

const editWebhook = async () => {
    isLoading.value = true
    try {
        await store.dispatch('telephony/editWebhookByNumber', {
            number: dialogParams.value?.number,
            webhooks: [{
                type: 'inbox',
                url: form.incoming,
            }],
            moshaToast: proxy.$moshaToast,
        })
    } finally {
        isLoading.value = false
    }
}

const onSubmit = async () => {
    if (!isSubmittable.value) return

    if (dialogParams.value?.action === 'add') {
        await addWebhook()
    } else {
        await editWebhook()
    }

    if (!!store.getters.getNextDialog) {
        store.commit("setDialog", store.getters.getNextDialog);
        store.commit("setNextDialog", "");
    } else {
        store.commit("closeDialog");
    }
}

watchEffect(() => {
    if (dialogParams.value.action === 'edit') {
        form.incoming = dialogParams.value.info.webhooks[0]?.url || ""
        initialIncoming.value = form.incoming
    }
})
</script>

<template>
    <slot name="header_close"/>

    <div class="wrapper_dialog">
        <h3 class="title">
            {{ $t('telephony.connected_numbers.webhooks.dialog_title') }}
            <div class="number-block">
                <CallNumberView :number="dialogParams.number"/>
            </div>
        </h3>

        <form class="content" novalidate @submit.prevent="onSubmit">
            <div class="field">
                <p class="subtitle">{{ $t('telephony.connected_numbers.webhooks.address') }}</p>
                <input
                    v-model="form.incoming"
                    :class="{ input_error: !!error }"
                    placeholder="http(s)://example.com"
                />
                <div class="input-errors" :class="{ 'is-shown': !!error }">{{ error }}</div>
            </div>

            <div class="action_container">
                <ButtonLoading
                    type="submit"
                    :loading="isLoading"
                    :disabled="isLoading || !isSubmittable"
                >
                    <template v-if="dialogParams.action === 'edit'">
                        {{ $t("dialog.webhooks.button") }}
                    </template>
                    <template v-else>
                        {{ $t("dialog.webhooks.add_button") }}
                    </template>
                </ButtonLoading>
            </div>
        </form>
    </div>
</template>

<style lang="scss" scoped>
@use "@/styles/dialog-form.scss";
</style>
